import { useMemo } from 'react';
import ReactDomServer from 'react-dom/server';
import { useTheme } from '@chakra-ui/react';

import { useHtmlTranslations, useTranslations } from '../../../../contexts/LocalizationContext';
import { useScannedRelevosContext } from '../../../../contexts/ScannedRelevosContext';
import { MoneyAmount } from '../../../../utils/money';
import { ScannerMode } from '../../../RelevoAssignment/model/enums/scanner-mode.enum';
import { useDepositResultContext } from '../../context/DepositResultContext/DepositResultContext';
import { isAnyItemExpired } from '../../utils/deposit.helper';

export const useDepositResultSummary = () => {
  const translations = useTranslations();
  const htmlTranslations = useHtmlTranslations();
  const { mode } = useScannedRelevosContext();
  const { deposit, successItems, failureItems } = useDepositResultContext();
  const { colors } = useTheme();

  const boldColor = useMemo(
    () => (mode === ScannerMode.DEPOSIT_ASSIGN ? colors.red['800'] : colors.green['700']),
    [mode, colors],
  );

  const itemsExpired = useMemo(() => isAnyItemExpired(successItems), [successItems]);

  const returnDepositSummaryTextTranslationKey = useMemo(() => {
    return itemsExpired ? 'return_deposit_summary_expired_items_text' : 'return_deposit_summary_text';
  }, [itemsExpired]);

  const returnedItemsAmount = useMemo(() => {
    return successItems.reduce((sum, item) => item.amount.add(sum), MoneyAmount.fromMinUnits(0));
  }, [successItems]);

  const expiredItemsAmount = useMemo(() => {
    return successItems
      .filter((item) => item.expiresAt && new Date(item.expiresAt) < new Date())
      .reduce((sum, item) => item.amount.add(sum), MoneyAmount.fromMinUnits(0));
  }, [successItems]);

  const totalItemsCount = useMemo(() => {
    return successItems.length + failureItems.length;
  }, [successItems.length, failureItems.length]);

  const summaryTextParams = useMemo(() => {
    return {
      '{{successItems}}': successItems.length.toString(),
      '{{allItems}}': totalItemsCount.toString(),
    };
  }, [successItems.length, totalItemsCount]);

  const summaryText = useMemo(() => {
    return mode === ScannerMode.DEPOSIT_ASSIGN
      ? translations('borrow_deposit_summary_text', summaryTextParams)
      : htmlTranslations(returnDepositSummaryTextTranslationKey, summaryTextParams);
  }, [mode, translations, htmlTranslations, returnDepositSummaryTextTranslationKey, summaryTextParams]);

  const depositAmount = useMemo(() => {
    return returnedItemsAmount.subtract(expiredItemsAmount);
  }, [returnedItemsAmount, expiredItemsAmount]);

  const depositSummaryAction = useMemo(
    () =>
      mode === ScannerMode.DEPOSIT_ASSIGN
        ? translations('borrow_deposit_summary_action')
        : translations('return_deposit_summary_action'),
    [mode, translations],
  );

  const depositSummaryTextArgs = useMemo(() => {
    return {
      '{{action}}': ReactDomServer.renderToString(
        <span style={{ fontWeight: 'bold', color: boldColor }}>{depositSummaryAction}</span>,
      ),
      '{{amount}}': ReactDomServer.renderToString(
        <span style={{ fontWeight: 'bold', color: boldColor }}>{depositAmount.toFormattedAmount()}</span>,
      ),
      '{{currency}}': ReactDomServer.renderToString(
        <span style={{ fontWeight: 'bold', color: boldColor }}>{deposit.currency.toString()}</span>,
      ),
    };
  }, [boldColor, depositSummaryAction, depositAmount, deposit.currency]);

  const depositSummaryText = useMemo(() => {
    return mode === ScannerMode.DEPOSIT_ASSIGN
      ? htmlTranslations('borrow_deposit_summary', depositSummaryTextArgs)
      : htmlTranslations('return_deposit_summary', depositSummaryTextArgs);
  }, [mode, htmlTranslations, depositSummaryTextArgs]);

  const depositIcon = useMemo(() => {
    return mode === ScannerMode.DEPOSIT_ASSIGN ? 'collect_money.svg' : 'give_money.svg';
  }, [mode]);

  return {
    itemsExpired,
    returnedItemsAmount,
    expiredItemsAmount,
    summaryText,
    depositSummaryText,
    depositAmount,
    deposit,
    successItems,
    failureItems,
    mode,
    totalItemsCount,
    depositIcon,
  };
};
